import { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { 
  Plus, 
  Pencil, 
  Trash2,
  Briefcase
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function FunctionsPage() {
  const [functions, setFunctions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingFunction, setEditingFunction] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
  });

  useEffect(() => {
    fetchFunctions();
  }, []);

  const fetchFunctions = async () => {
    try {
      const response = await fetch(`${API_URL}/api/functions`, { credentials: 'include' });
      if (response.ok) {
        setFunctions(await response.json());
      }
    } catch (error) {
      console.error('Error fetching functions:', error);
      toast.error('Erro ao carregar funções');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.name) {
      toast.error('Nome é obrigatório');
      return;
    }

    try {
      const url = editingFunction 
        ? `${API_URL}/api/functions/${editingFunction.function_id}`
        : `${API_URL}/api/functions`;
      
      const response = await fetch(url, {
        method: editingFunction ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        toast.success(editingFunction ? 'Função atualizada' : 'Função criada');
        setDialogOpen(false);
        resetForm();
        fetchFunctions();
      } else {
        toast.error('Erro ao salvar função');
      }
    } catch (error) {
      console.error('Error saving function:', error);
      toast.error('Erro ao salvar função');
    }
  };

  const handleDelete = async (functionId) => {
    try {
      const response = await fetch(`${API_URL}/api/functions/${functionId}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (response.ok) {
        toast.success('Função excluída');
        fetchFunctions();
      } else {
        toast.error('Erro ao excluir função');
      }
    } catch (error) {
      console.error('Error deleting function:', error);
      toast.error('Erro ao excluir função');
    } finally {
      setDeleteConfirmOpen(false);
      setDeleteTarget(null);
    }
  };

  const confirmDelete = (functionId) => {
    setDeleteTarget(functionId);
    setDeleteConfirmOpen(true);
  };

  const handleEdit = (func) => {
    setEditingFunction(func);
    setFormData({ name: func.name });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingFunction(null);
    setFormData({ name: '' });
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in" data-testid="functions-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-zinc-50 tracking-tight flex items-center gap-3">
              <Briefcase className="w-8 h-8" />
              Funções
            </h1>
            <p className="text-zinc-400 mt-1">Gerencie as funções disponíveis</p>
          </div>

          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button className="bg-white text-black hover:bg-zinc-200" data-testid="add-function-btn">
                <Plus className="w-4 h-4 mr-2" />
                Nova Função
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-zinc-900 border-zinc-800">
              <DialogHeader>
                <DialogTitle className="text-zinc-100">
                  {editingFunction ? 'Editar Função' : 'Nova Função'}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 pt-4">
                <div className="space-y-2">
                  <Label className="text-zinc-400">Nome *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="Nome da função"
                    className="bg-zinc-950 border-zinc-800"
                    data-testid="function-name-input"
                  />
                </div>

                <Button 
                  onClick={handleSubmit}
                  className="w-full bg-white text-black hover:bg-zinc-200"
                  data-testid="save-function-btn"
                >
                  {editingFunction ? 'Salvar Alterações' : 'Criar Função'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Functions Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {functions.length === 0 ? (
            <Card className="bg-zinc-900 border-zinc-800 col-span-full">
              <CardContent className="py-12 text-center text-zinc-500">
                Nenhuma função cadastrada
              </CardContent>
            </Card>
          ) : (
            functions.map((func) => (
              <Card 
                key={func.function_id} 
                className="bg-zinc-900 border-zinc-800"
                data-testid={`function-card-${func.function_id}`}
              >
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-zinc-800 flex items-center justify-center">
                        <Briefcase className="w-5 h-5 text-zinc-400" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-zinc-200">{func.name}</h3>
                        <p className="text-xs text-zinc-500">Função</p>
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleEdit(func)}
                        className="h-8 w-8 text-zinc-400 hover:text-zinc-200"
                        data-testid={`edit-function-${func.function_id}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => confirmDelete(func.function_id)}
                        className="h-8 w-8 text-zinc-400 hover:text-red-400"
                        data-testid={`delete-function-${func.function_id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
          <AlertDialogContent className="bg-zinc-900 border-zinc-800">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-zinc-100">Confirmar exclusão</AlertDialogTitle>
              <AlertDialogDescription className="text-zinc-400">
                Tem certeza que deseja excluir esta função? Esta ação não pode ser desfeita.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="bg-zinc-800 border-zinc-700 text-zinc-300 hover:bg-zinc-700">
                Cancelar
              </AlertDialogCancel>
              <AlertDialogAction 
                onClick={() => deleteTarget && handleDelete(deleteTarget)}
                className="bg-red-600 text-white hover:bg-red-700"
              >
                Excluir
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
}
